import { Table } from 'antd';
import { rightsService } from "../../../../services/rightsService";
import { forwardRef, useEffect, useState } from 'react';
import { displayNumbers } from '../../../../variables/numbersFunctions';
import { frenchMonth, frenchDateOnly, formatJustificatifCheque } from '../../../../variables/functions';
import { exportBordereauExcel, moisPeriodeLabel } from './bordereauExcel';
import { getCompagnieLogo } from '../../../../hooks/useFilesHooks';
import BordereauPrintTable from './BordereauPrintTable';

const BordereauSinistresPayes = forwardRef(({
    data,
    columns,
    selectedRows,
    onSelectionChange,
    onValidate,
    onPrint,
    isValidated,
    isDisabled,
    loading,
    categorie,
    periode,
    compagnie
}, ref) => {

    const [isPrinting, setIsPrinting] = useState(false);
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);

    // Logo de la compagnie pour l'en-tête répété (BordereauPrintTable).
    useEffect(() => {
        if (compagnie?.logo && compagnie?.code) {
            getCompagnieLogo(compagnie.code)
                .then(async (response) => {
                    const imageBytes = await response.arrayBuffer();
                    const blob = new Blob([imageBytes], { type: 'image/png' });
                    setLogoPreviewUrl(URL.createObjectURL(blob));
                })
                .catch((e) => console.log(e));
        }
    }, [compagnie]);

    // Sélection par défaut = TOUTES les lignes (défaut « on cède l'intégralité »), MÊME quand les
    // cases sont désactivées (Pool/consultation ou sans habilitation HLA099). Sinon le TOTAL — qui ne
    // somme que les lignes sélectionnées — restait à 0 alors que des lignes s'affichaient. On n'auto-
    // sélectionne pas une cession DÉJÀ validée (elle porte sa propre sélection).
    useEffect(() => {
        if (data.length > 0 && selectedRows.length === 0 && !isValidated) {
            onSelectionChange(data); // toutes les lignes sélectionnées par défaut
        }
    }, [data, selectedRows, onSelectionChange, isValidated]);

    // Décocher des lignes pendant une cession est SOUS HABILITATION (HLA099).
    // Sans le droit : cases désactivées → l'utilisateur cède l'intégralité, sans
    // pouvoir en retirer.
    const peutDecocher = rightsService("HLA099");

    // TOTAL : en création on somme les lignes SÉLECTIONNÉES. En vue d'une cession VALIDÉE affichée sans
    // sélection transmise (ex. détail /toutes-les-cessions, selectedRows=[]), toutes les lignes affichées
    // sont celles qui ont été cédées → on les compte toutes (sinon le TOTAL restait à 0).
    const totalToutesLignes = selectedRows.length === 0 && isValidated;
    const estInclus = (item) => totalToutesLignes
        || !!selectedRows.find((r) => (r.codeId || r.key) === (item.codeId || item.key));

    const rowSelection = {
        selectedRowKeys: selectedRows.map(r => r.key || r.codeId),
        onChange: (selectedRowKeys, selectedRows) => {
            onSelectionChange(selectedRows);
        },
        getCheckboxProps: () => ({
            disabled: isValidated || isDisabled || !peutDecocher,
        }),
    };

    const handlePrint = () => {
        setIsPrinting(true);
        // Petit délai pour s'assurer que le DOM est mis à jour
        setTimeout(() => {
            onPrint();
            // Remettre l'état après l'impression
            setTimeout(() => setIsPrinting(false), 100);
        }, 100);
    };

    const handleExportExcel = () => {
        const total = (data || []).reduce(
            (sum, item) => sum + (estInclus(item) ? (item.montant || 0) : 0), 0);
        exportBordereauExcel({
            titre: `BORDEREAU DES SINISTRES PAYES DU MOIS DE ${moisPeriodeLabel(periode).toUpperCase()}`,
            metaLines: [`Catégorie ${categorie?.codeCategorie || ''}`, `Compagnie : ${compagnie?.raisonSociale || ''}`],
            headers: ['N° Sinistre', 'N° Police', 'Assuré', 'Date survenance', 'Catégorie', 'Justificatif', 'Montant'],
            rows: (data || []).map((it) => [
                it.sinistreNumeroSinistre || it.sinistreNumSinistre, it.sinistreNumPolice || it.sinistreNumPoliceP,
                it.sinistreSouscripteur, it.dateSinistre ? frenchDateOnly(it.dateSinistre) : '', it.categorie, formatJustificatifCheque(it), Math.trunc(it.montant || 0),
            ]),
            totalRow: ['TOTAL', '', '', '', '', '', Math.trunc(total)],
            nomFichier: `Bordereau_Sinistres_Payes_${categorie?.codeCategorie || ''}_${moisPeriodeLabel(periode)}`,
        });
    };

    return (
        <div>
            {/* Version affichage */}
            <div className="mb-4" style={{ padding: 15 }}>
                <Table
                    rowKey={(record) => record.key || record.codeId}
                    rowSelection={rowSelection}
                    columns={columns}
                    dataSource={data}
                    pagination={{ pageSize: 10 }}
                    summary={() => (
                        <Table.Summary fixed>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={6}>
                                    <strong>TOTAL</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={1}>
                                    <strong>{displayNumbers(data?.reduce(
                                        (sum, item) =>
                                            sum + (estInclus(item) ? (item.montant || 0) : 0),
                                        0
                                    ) || 0)} F CFA</strong>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    )}
                />

                <div className="mt-3 d-flex gap-2">
                    <button
                        type="button"
                        onClick={onValidate}
                        disabled={isDisabled || isValidated || loading}
                        className={isValidated ? "btn btn-success" : "btn btn-danger"}
                    >
                        {loading && (
                            <span className="spinner-grow spinner-grow-sm me-1" role="status" aria-hidden="true"></span>
                        )}
                        <i className="tf-icons bx bxs-badge-check bx-18px me-2"></i>
                        <span className="align-middle">
                            {loading ? 'Validation...' : isValidated
                                ? `Sinistres payés ${categorie?.codeCategorie} de ${periode ? frenchMonth(periode?.split("-")?.[1]) + " " + periode.split("-")?.[0] : ''} validés`
                                : `Valider les sinistres payés ${categorie?.codeCategorie} de ${periode ? frenchMonth(periode?.split("-")?.[1]) + " " + periode?.split("-")?.[0] : ''}`
                            }
                        </span>
                    </button>

                    <button
                        type="button"
                        onClick={handlePrint}
                        className="btn btn-label-pinterest"
                    >
                        <i className="tf-icons bx bxs-file-pdf bx-18px me-2"></i>
                        Imprimer (PDF)
                    </button>

                    <button
                        type="button"
                        onClick={handleExportExcel}
                        className="btn btn-label-success"
                    >
                        <i className="tf-icons bx bxs-file-export bx-18px me-2"></i>
                        Exporter Excel
                    </button>
                </div>
            </div>

            {/* Version impression (cachée) */}
            <div ref={ref} style={{
                position: isPrinting ? 'relative' : 'absolute',
                left: isPrinting ? 'auto' : '-9999px',
                visibility: isPrinting ? 'visible' : 'hidden'
            }}>
                <style>
                    {`
                        @media print {
                            @page {
                                size: landscape;
                                margin: 20mm;
                            }
                            body {
                                print-color-adjust: exact;
                                -webkit-print-color-adjust: exact;
                            }
                        }
                    `}
                </style>
                <div style={{ padding: '10mm 8mm', fontFamily: 'Arial, sans-serif' }}>
                    <BordereauPrintTable
                        titre="BORDEREAU DES SINISTRES PAYES"
                        categorie={categorie}
                        compagnie={compagnie}
                        periode={periode}
                        logoPreviewUrl={logoPreviewUrl}
                        data={data}
                        rowKey="codeId"
                        columns={[
                            { title: 'N° Sinistre', get: (it) => it.sinistreNumeroSinistre || it.sinistreNumSinistre },
                            { title: 'N° Police', get: (it) => it.sinistreNumPolice || it.sinistreNumPoliceP },
                            { title: 'Assuré', get: (it) => it.sinistreSouscripteur },
                            { title: 'Date survenance', get: (it) => (it.dateSinistre ? frenchDateOnly(it.dateSinistre) : '') },
                            { title: 'Catégorie', get: (it) => it.categorie },
                            { title: 'Justificatif', get: (it) => formatJustificatifCheque(it) },
                            { title: 'Montant', align: 'right', get: (it) => displayNumbers(it.montant) },
                        ]}
                        totalValue={`${displayNumbers((data || []).reduce((sum, item) => sum + (estInclus(item) ? (item.montant || 0) : 0), 0) || 0)} F CFA`}
                    />
                </div>
            </div>
        </div>
    );
});

BordereauSinistresPayes.displayName = 'BordereauSinistresPayes';

export default BordereauSinistresPayes;